import React, { useEffect, useRef, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Button,
  Card,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Box,
  Pagination,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  addArticleCategory,
  editArticleCategory,
} from "app/services/api/kiosk";
import CustomerItem from "./CustomerItem";
import DsokCustomerPreviewModal from "app/pages/customers/dsok/DsokCustomerPreviewModal";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CustomerForm from "./CustomerForm";
import { getCustomers, triggerDSOKBuild } from "app/services/api/management";
import {
  getServicePartnerCustomers,
  getServicePartners,
} from "app/services/api/servicePartners";
import { loginAsCustomer } from "app/services/loginAsCustomer";
import { useNavigate, useSearchParams } from "react-router-dom";

const ITEM_PER_PAGE = 10;

const ENV_PRODUCTION = "production";
const ENV_STAGING = "staging";
const VALID_ENVS = [ENV_PRODUCTION, ENV_STAGING];

const DSOK_STORAGE_KEYS = { env: "dsok.customers.env", page: "dsok.customers.page" };

function getStoredDsokEnv() {
  try {
    const s = localStorage.getItem(DSOK_STORAGE_KEYS.env);
    return VALID_ENVS.includes(s) ? s : null;
  } catch (_) {
    return null;
  }
}

function setStoredDsokEnv(value) {
  try {
    if (VALID_ENVS.includes(value)) localStorage.setItem(DSOK_STORAGE_KEYS.env, value);
  } catch (_) {}
}

function getStoredDsokPage() {
  try {
    const p = parseInt(localStorage.getItem(DSOK_STORAGE_KEYS.page), 10);
    return Number.isFinite(p) && p >= 1 ? p : null;
  } catch (_) {
    return null;
  }
}

function setStoredDsokPage(value) {
  try {
    const n = Number(value);
    if (Number.isFinite(n) && n >= 1) localStorage.setItem(DSOK_STORAGE_KEYS.page, String(n));
  } catch (_) {}
}

function getBeDomain(c) {
  if (typeof c?.beDomain === "string") return c.beDomain;
  try {
    const parsed =
      typeof c?.dsokSettings === "string"
        ? JSON.parse(c.dsokSettings)
        : c?.dsokSettings;
    return parsed?.customer?.beDomain ?? "";
  } catch (_) {
    return "";
  }
}

function isStagingCustomer(c) {
  const beDomain = getBeDomain(c);
  return (
    typeof beDomain === "string" && beDomain.toLowerCase().includes("staging")
  );
}

const Customers = ({ dsok = false }) => {
  const { getAuthUser, setAuthToken } = useJumboAuth();
  const { customerId, customer, type: userType } = getAuthUser() || {};
  const superadmin = customer?.customerType === 1;
  // Service partner visibility rule: show only customers assigned via servicePartnerId
  const isServicePartner =
    customer?.customerType === 7 || customer?.customer_type === 7;
  const loggedInCustomerId = customer?.id ?? customerId;
  const { t } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const [searchName, setSearchName] = useState();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const envParam = searchParams.get("env");
  const env = VALID_ENVS.includes(envParam) ? envParam : ENV_PRODUCTION;

  const PAGE_STORAGE_KEY = dsok ? "customersPage_dsok" : "customersPage_all";
  const [page, setPage] = useState(() => {
    if (dsok) {
      const stored = getStoredDsokPage();
      if (stored != null) return stored;
    }
    const saved = Number(sessionStorage.getItem(PAGE_STORAGE_KEY));
    return !Number.isNaN(saved) && saved > 0 ? saved : 1;
  });

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const setEnvFilter = (value) => {
    if (!VALID_ENVS.includes(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("env", value);
        return next;
      },
      { replace: true },
    );
    setPage(1);
    setStoredDsokEnv(value);
    setStoredDsokPage(1);
  };

  // Service Partner mode: use getServicePartners instead of default loadCustomers
  const loadCustomers = async () => {
    if (dsok && isServicePartner) {
      try {
        const raw = await getServicePartnerCustomers(customerId);
        const list = Array.isArray(raw) ? raw : [];
        const customersData = list.map((item) => {
          if (item && item.customerData) {
            return { ...item.customerData, users: item.users ?? [] };
          }
          return {
            ...item,
            companyName: item.companyName ?? item.company_name,
            users: item.users ?? [],
          };
        });
        // DSOK: store full list; search/env filtering is done in displayCustomers
        setCustomers(customersData || []);
      } finally {
        setLoading(false);
      }
      return;
    }

    await getCustomers(customerId).then((customers) => {
      const customersData = (customers || []).map((customer) => ({
        ...customer.customerData,
        users: customer.users,
      }));

      let filteredData = customersData;
      if (dsok) {
        filteredData = customersData.filter((customer) => {
          return customer.dsok === true || customer.dsok === 1;
        });
      }
      // DSOK: store full list; search/env filtering is done in displayCustomers.
      if (dsok) {
        setCustomers(filteredData || []);
      } else {
        const filteredCustomers = searchName
          ? filteredData.filter((c) =>
              (c.companyName || "")
                .toLowerCase()
                .includes((searchName || "").toLowerCase()),
            )
          : filteredData;
        setCustomers(filteredCustomers || []);
      }
    });
    setLoading(false);
  };

  const prevSearchRef = useRef(searchName);
  useEffect(() => {
    const prev = prevSearchRef.current;
    prevSearchRef.current = searchName;
    // If this is the initial transition from undefined to empty, don't reset page
    const prevWasUndefined = typeof prev === "undefined";
    const currIsEmpty = searchName == null || String(searchName).trim() === "";
    if (prevWasUndefined && currIsEmpty) {
      return;
    }
    if (prev !== searchName) {
      setLoading(true);
      setPage(1);
      loadCustomers();
    }
  }, [searchName]);

  // DSOK: restore env from localStorage when URL has no valid env (so refresh/navigate-back keeps selection)
  useEffect(() => {
    if (!dsok) return;
    const urlEnv = searchParams.get("env");
    if (VALID_ENVS.includes(urlEnv)) return;
    const stored = getStoredDsokEnv();
    if (stored) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("env", stored);
          return next;
        },
        { replace: true },
      );
    }
  }, [dsok, searchParams, setSearchParams]);

  // Initial load and reload when key inputs change. Service Partner mode: use getServicePartners instead of default loadCustomers
  useEffect(() => {
    setLoading(true);
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, dsok, isServicePartner]);

  // DSOK: apply env filter (Production/Staging) when no search; when search is active, search across all.
  // Non-DSOK: show customers as loaded.
  const displayCustomers = React.useMemo(() => {
    if (!dsok) return customers;
    const searchTerm = (searchName ?? "").trim();
    const hasSearch = searchTerm.length > 0;
    if (hasSearch) {
      return customers.filter((c) =>
        (c.companyName || "").toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    const isProd = env === ENV_PRODUCTION;
    return customers.filter((c) =>
      isProd ? !isStagingCustomer(c) : isStagingCustomer(c),
    );
  }, [customers, dsok, searchName, env]);

  // Clamp page after data is loaded to avoid jumping to 1 prematurely
  useEffect(() => {
    if (loading) return;
    const total = displayCustomers?.length || 0;
    if (total === 0) return;
    const maxPage = Math.max(1, Math.ceil(total / ITEM_PER_PAGE));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [displayCustomers, page, loading]);

  // Persist page changes (sessionStorage for all; localStorage for DSOK so it survives refresh/navigate)
  useEffect(() => {
    sessionStorage.setItem(PAGE_STORAGE_KEY, String(page));
    if (dsok) setStoredDsokPage(page);
  }, [page, PAGE_STORAGE_KEY, dsok]);

  const openModal = (type, customer) => {
    showDialog({
      variant: "default",
      title: t(`pages.customers.${type}`),
      sx: {
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      },
      content: (
        <CustomerForm
          customer={customer}
          onAddCustomer={handleAddCustomer}
          onEditCustomer={handleEditCustomer}
          onCancel={hideDialog}
        />
      ),
    });
  };

  const handleLoginAsCustomer = async (customer) => {
    setLoading(true);
    try {
      await loginAsCustomer(customer, {
        setAuthToken,
        navigate,
        enqueueSnackbar,
        t,
      });
    } catch {
      const message = `${t("buttons.loginAsCustomer")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (customer) => {
    setLoading(true);
    hideDialog();
    try {
      await addArticleCategory(customer);
      await loadCustomers();
      const message = `${t("buttons.add")} ${t("notifications.wasSuccessful")}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.add")} ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };
  const handleEditCustomer = async (customer) => {
    setLoading(true);
    hideDialog();
    try {
      await editArticleCategory(customer);
      await loadCustomers();
      const message = `${t("notifications.wasSuccessful")}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };
  const handleBuildAll = async () => {
    for (const customer of customers) {
      await triggerDSOKBuild(customer);
    }
    setLoading(true);
    try {
      const message = `${t("pages.buildAndDeploy.buildAndDeployRequest")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
      setLoading(false);
    } catch (error) {
      const message = `${t("pages.buildAndDeploy.buildAndDeployRequest")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
    hideDialog();
  };
  const handleBuild = async (customer) => {
    setLoading(true);
    try {
      await triggerDSOKBuild(customer);
      const message = `${t("pages.buildAndDeploy.buildAndDeployRequest")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
      setLoading(false);
    } catch (error) {
      const message = `${t("pages.buildAndDeploy.buildAndDeployRequest")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
    hideDialog();
  };

  const buildConfirmation = (customer) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      onYes: () =>
        customer === "all" ? handleBuildAll() : handleBuild(customer),
      onNo: () => hideDialog(),
    });
  };

  const openPreviewModal = (customer) => {
    showDialog({
      variant: "default",
      title: "Informácie o zákazníkovi",
      sx: {
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
        "& .MuiDialog-paper": {
          minWidth: 500,
          maxWidth: "45vw",
        },
      },
      content: (
        <DsokCustomerPreviewModal customer={customer} onClose={hideDialog} />
      ),
    });
  };

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("sidebar.menuItem.customers")}
        {dsok ? " - DSOK" : ""}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        justifyContent={"space-between"}
        mb={2}
        spacing={1}
      >
        <Box
          width={dsok ? "100%" : "50%"}
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={1}
          sx={dsok ? { minWidth: 0 } : {}}
        >
          <Card
            sx={{
              mb: 0,
              flex: dsok ? "0 1 33%" : undefined,
              width: dsok ? "33%" : "100%",
              minWidth: 0,
            }}
          >
            <JumboSearch
              page="customers"
              onChange={setSearchName}
              sx={{ width: "100%" }}
              placeholder={t("pages.presentation.searchPresentation")}
            />
          </Card>
          <Tooltip title={t("pages.customer.addCustomer")}>
            <span>
              <IconButton
                disabled={!superadmin || loading}
                onClick={() => navigate("/dsok/customers/new")}
              >
                <AddBoxIcon />
              </IconButton>
            </span>
          </Tooltip>
          {dsok && (
            <>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                  size="small"
                  variant={env === ENV_PRODUCTION ? "contained" : "outlined"}
                  onClick={() => setEnvFilter(ENV_PRODUCTION)}
                >
                  {t("pages.dsokCustomers.env.production", {
                    defaultValue: "Production",
                  })}
                </Button>
                <Button
                  size="small"
                  variant={env === ENV_STAGING ? "contained" : "outlined"}
                  onClick={() => setEnvFilter(ENV_STAGING)}
                >
                  {t("pages.dsokCustomers.env.staging", {
                    defaultValue: "Staging",
                  })}
                </Button>
              </Stack>
              <Pagination
                count={Math.ceil(displayCustomers?.length / ITEM_PER_PAGE)}
                color="primary"
                page={page}
                onChange={(_, val) => setPage(val)}
                sx={{ flexShrink: 0, ml: "auto" }}
              />
            </>
          )}
        </Box>
        {!dsok && (
          <Pagination
            count={Math.ceil(displayCustomers?.length / ITEM_PER_PAGE)}
            color="primary"
            page={page}
            onChange={(_, val) => setPage(val)}
          />
        )}
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : (
        <>
          {displayCustomers?.length ? (
            displayCustomers.map((customer, index) =>
              index + 1 > ITEM_PER_PAGE * (page - 1) &&
              index + 1 <= ITEM_PER_PAGE * page ? (
                <CustomerItem
                  key={index}
                  item={customer}
                  superadmin={superadmin}
                  disabled={loading}
                  onLogin={handleLoginAsCustomer}
                  onBuild={() => buildConfirmation(customer)}
                  onPreview={openPreviewModal}
                  dsok={dsok}
                  userType={userType}
                />
              ) : null,
            )
          ) : (
            <JumboListNoDataPlaceHolder />
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default Customers;
